import React from 'react';
import styled, { keyframes } from 'styled-components';

import { buildImgixSrc } from 'shared/components/imgix';
import useTranslation from 'hooks/use-translation';

import ROUTES from 'src/routes';

import { palette } from './palette';
import { Container, OutlineLink, PillLink, PillTag, RevealOnScroll, Section, SectionHead } from './primitives';

const MAP_IMAGE_SRC = buildImgixSrc(`https://images.dutchie.com/home-redesign/neighborhood-map.png`, {
  auto: `format,compress`,
});
const GROW_IMAGE_SRC = buildImgixSrc(`https://images.dutchie.com/home-redesign/grow-dispensary.png`, {
  auto: `format,compress`,
});

type CannabisYourWaySectionProps = {
  onCtaClick: () => void;
};

const MAP_MARKERS = [
  { cx: 92, cy: 48, delays: [`0s`, `1.3s`] },
  { cx: 270, cy: 128, delays: [`0.4s`, `1.7s`] },
  { cx: 450, cy: 55, delays: [`0.8s`, `2.1s`] },
  { cx: 500, cy: 135, delays: [`1.2s`, `2.5s`] },
  { cx: 320, cy: 195, delays: [`1.6s`, `0.3s`] },
];

export function CannabisYourWaySection({ onCtaClick }: CannabisYourWaySectionProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <Section>
      <Container>
        <SectionHead
          first={t('home.redesign.yourWayFirst', 'Cannabis')}
          rest={t('home.redesign.yourWayRest', 'your way')}
        />
        <RevealOnScroll>
          <Split>
            <Card>
              <MapBox aria-hidden='true'>
                <svg viewBox='0 0 600 220' preserveAspectRatio='xMidYMid slice'>
                  {MAP_MARKERS.map((marker) => (
                    <g key={`${marker.cx}-${marker.cy}`}>
                      <Ring cx={marker.cx} cy={marker.cy} r='5' $delay={marker.delays[0]} />
                      <Ring cx={marker.cx} cy={marker.cy} r='5' $delay={marker.delays[1]} />
                      <circle cx={marker.cx} cy={marker.cy} r='5' fill='#0d2a23' />
                    </g>
                  ))}
                  <circle cx='180' cy='175' r='5' fill='#0d2a23' />
                  <circle cx='385' cy='88' r='5' fill='#0d2a23' />
                </svg>
              </MapBox>
              <CardBody>
                <PillTag>{t('home.redesign.shopNow', 'Shop now')}</PillTag>
                <h3>{t('home.redesign.findYourDispensary', 'Find your dispensary')}</h3>
                <p>
                  {t(
                    'home.redesign.findYourDispensaryBody',
                    'Browse menus, order ahead, earn rewards at dispensaries near you.'
                  )}
                </p>
                <SearchButton type='button' onClick={onCtaClick}>
                  <span className='pin'>
                    <svg
                      width='20'
                      height='20'
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    >
                      <path d='M12 21s-7-6.5-7-12a7 7 0 0 1 14 0c0 5.5-7 12-7 12z' />
                      <circle cx='12' cy='9' r='2.5' />
                    </svg>
                  </span>
                  <span className='placeholder'>{t('home.redesign.enterAddress', 'Enter address or zip code')}</span>
                  <span className='cta'>{t('common.search', 'Search')}</span>
                </SearchButton>
              </CardBody>
            </Card>
            <Card>
              <CardImage style={{ backgroundImage: `url('${GROW_IMAGE_SRC}')` }} />
              <CardBody>
                <PillLink href={ROUTES.DISPENSARY_B2B}>{t('common.forBusiness', 'For business')}</PillLink>
                <h3>{t('home.redesign.growYourDispensary', 'Grow your dispensary')}</h3>
                <p>
                  {t(
                    'home.redesign.growYourDispensaryBody',
                    'Reach more customers with online ordering, real-time menus, and the tools to turn first-timers into regulars.'
                  )}
                </p>
                <OutlineLink href={ROUTES.DISPENSARY_B2B} style={{ alignSelf: `flex-start` }}>
                  {t('home.redesign.seeItInAction', 'See it in action')}
                </OutlineLink>
              </CardBody>
            </Card>
          </Split>
        </RevealOnScroll>
      </Container>
    </Section>
  );
}

const pulseRing = keyframes`
  0%   { r: 5;  opacity: 0.9; stroke-width: 2.5; }
  100% { r: 60; opacity: 0; stroke-width: 0.8; }
`;

const Ring = styled.circle<{ $delay: string }>`
  fill: none;
  stroke: #7bc689;
  stroke-width: 2.5;
  transform-box: fill-box;
  transform-origin: center;
  animation: ${pulseRing} 2.6s ease-out infinite;
  animation-delay: ${({ $delay }) => $delay};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 0.35;
  }
`;

const Split = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;

  ${({ theme }) => theme.breakpoints.down(`xs`)} {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: ${palette.bgCard};
  border-radius: 18px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const MapBox = styled.div`
  height: 220px;
  overflow: hidden;
  position: relative;
  background: url('${MAP_IMAGE_SRC}') center/cover no-repeat #ede6d6;

  svg {
    display: block;
    width: 100%;
    height: 100%;
    position: absolute;
    inset: 0;
  }
`;

const CardImage = styled.div`
  height: 220px;
  background-size: cover;
  background-position: center;
`;

const CardBody = styled.div`
  padding: 30px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;

  > span,
  > a:first-child {
    align-self: flex-start;
  }

  h3 {
    margin: 0;
    font-size: 26px;
    color: ${palette.cream};
  }

  p {
    margin: 0;
    color: ${palette.creamDim};
    font-size: 15px;
    line-height: 1.5;
  }
`;

const SearchButton = styled.button`
  margin-top: auto;
  display: flex;
  align-items: center;
  background: ${palette.inputBg};
  border: 1px solid ${palette.inputBorder};
  border-radius: 12px;
  padding: 6px;
  cursor: pointer;
  font-family: inherit;
  text-align: left;

  .pin {
    padding: 0 8px 0 14px;
    color: ${palette.goldFirst};
    display: inline-flex;
    align-items: center;
  }

  .placeholder {
    flex: 1;
    color: ${palette.inputPlaceholder};
    font-size: 15px;
    padding: 12px 8px;
  }

  .cta {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    padding: 10px 22px;
    font-weight: 600;
    font-size: 15px;
    background: ${palette.gold};
    color: ${palette.textOnGold};
  }

  &:hover .cta {
    background: ${palette.goldBright};
  }
`;
